import Axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { CookieHelper } from 'Utils/cookieHelper'
import { COOKIE_KEYS } from 'Consts/cookies.enum'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { TOASTS } from 'Components/Elements/Toaster/Toast.models'
import { ToasterType } from 'Types/ToasterTypes'
import { translationLookUp } from 'Utils/getErrorMessage'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { ALLOWED_NON_AUTHENTICATED_PATHS, verifyTokenExpired } from 'Utils/token'

function authRequestInterceptor(config: AxiosRequestConfig) {
  const { getCookie } = CookieHelper()
  const token = getCookie(COOKIE_KEYS.TOKEN)
  const authCode = getCookie(COOKIE_KEYS.AUTH_CODE)
  config.headers = config.headers ?? {}
  if (authCode) {
    const cookieBeforeParse = authCode
    if (cookieBeforeParse) {
      const authCodeCookie = JSON.parse(cookieBeforeParse) as {
        authCode: string
        userId: number
      }
      if (authCodeCookie.authCode) {
        config.headers.common['x-auth-code'] = authCodeCookie.authCode || ''
      }
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  config.headers.Accept = 'application/json'

  return config
}

export const axios = Axios.create({
  baseURL: process.env.REACT_APP_CC_CORE,
})

export interface ServerError {
  httpStatusCode: number
  message: string
  path: string
  result: 'failed' | 'success'
  timestamp: string
}

export type ApiError<D = any> = AxiosError<ServerError, D>

axios.interceptors.request.use(authRequestInterceptor)
axios.interceptors.response.use(
  (response) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return response
  },
  (error: ApiError) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (
      (error.response?.status === 401 || error.response?.status === 403) &&
      !error.response?.config.url?.includes('auth') &&
      !error.response?.config.url?.includes('verify') &&
      !error.response?.config.url?.includes('validate')
    ) {
      Object.keys(COOKIE_KEYS).forEach((key: string) => {
        if (key !== 'AUTH_CODE') {
          CookieHelper().deleteCookie(COOKIE_KEYS[key] as string)
        }
      })
      useUserStore.getState()?.logOut()
    }
    return Promise.reject(error)
  }
)
interface RequestConfig<R> extends AxiosRequestConfig<R> {
  hideErrorToaster?: boolean
  onSuccessToast?: {
    message: string
    type?: ToasterType
  }
}

export const request = async <T, R = any>(config: RequestConfig<R>): Promise<T | ApiError> => {
  // eslint-disable-next-line react-hooks/rules-of-hooks, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
  const showToaster = useAppStore.getState().showToaster
  const { getCookie } = CookieHelper()
  const token = getCookie(COOKIE_KEYS.TOKEN)
  const isTokenValid = token ? !verifyTokenExpired(token) : false

  if (!isTokenValid && !ALLOWED_NON_AUTHENTICATED_PATHS.includes(config.url ?? '')) {
    Object.keys(COOKIE_KEYS).forEach((key: string) => {
      if (key !== 'AUTH_CODE') {
        CookieHelper().deleteCookie(COOKIE_KEYS[key] as string)
      }
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { hideErrorToaster, onSuccessToast, baseURL, ...axiosConfig } = config
  try {
    const response = await axios({ ...axiosConfig })
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    config.onSuccessToast &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      showToaster(config.onSuccessToast.message, config.onSuccessToast.type ?? TOASTS.success)
    return response.data as T
  } catch (error) {
    const err = error as ApiError
    console.error('Gateway error', err)
    if (!config.hideErrorToaster && err?.response?.data?.message) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      showToaster(translationLookUp[err.response.data.message], TOASTS.failure)
    }
    return Promise.reject(err)
  }
}
